/**
 * Turn a pasted page URL into the path the form actually wants.
 *
 * Both call sites (the Path field when adding or editing a page, and the manual
 * sitemap URL field) render the site's base URL as a static prefix beside the
 * input, so a user pasting a full URL ends up with the domain twice. These
 * helpers strip it back off.
 *
 * The comparison is deliberately lenient about how the URL is spelled: a URL
 * copied out of an address bar routinely differs from the stored base_url by
 * its scheme, a "www." prefix, or case, and all three used to defeat the strip.
 * Both sides are normalised the same way before they are compared, so the two
 * cannot drift.
 *
 * It is deliberately strict about which site the URL belongs to. A paste on any
 * other host, including another subdomain or another port, is returned as null
 * and left exactly as the user pasted it: that is a mistake worth seeing, not
 * one worth silently rewriting into a path.
 *
 * A query string and a fragment survive the strip. Deciding which query
 * parameters identify a distinct page is App\Utils\PagePath's job on the
 * server, not a paste handler's.
 */

// Only the two schemes a page URL can carry. A wider pattern reads a host out
// of "javascript://site.test/%0aalert(1)", "data://site.test/x" and the like,
// and hands back a path as though the paste named a page of this site.
const SCHEME = /^https?:\/\//i;
// The negative lookahead keeps a schemeless "localhost:8000/x" out of this —
// there the colon introduces a port, not a scheme.
const ANY_SCHEME = /^[a-z][a-z0-9+.-]*:(?!\d)/i;

/**
 * Split a URL-ish string into its authority and everything after it.
 *
 * Returns null when the string carries no authority — a bare path such as
 * "/contact" or "contact", which is already what the field wants — and when it
 * carries a scheme that is not http(s).
 *
 * `hadAuthority` says whether a scheme or a leading "//" proved this is a URL,
 * rather than a dotted string that merely looks like one. normalizeAuthority()
 * needs it to decide whether an "@" is userinfo or an email address.
 */
function splitUrlish(value) {
    let rest = value;
    let hadAuthority = false;

    if (SCHEME.test(rest)) {
        rest = rest.replace(SCHEME, '');
        hadAuthority = true;
    } else if (ANY_SCHEME.test(rest)) {
        return null;
    } else if (rest.startsWith('//')) {
        rest = rest.slice(2);
        hadAuthority = true;
    } else if (rest.startsWith('/')) {
        return null;
    }

    // A backslash ends the authority exactly as a slash does: browsers treat
    // "https://evil.com\@site.test/x" as a URL on evil.com, so reading past it
    // would let another site's URL be rewritten into a path of this one.
    const boundary = rest.search(/[/\\?#]/);
    const authority = boundary === -1 ? rest : rest.slice(0, boundary);
    const path = boundary === -1 ? '' : rest.slice(boundary);

    if (authority === '') {
        return null;
    }

    // With no scheme to prove it, only something host-shaped counts as an
    // authority: a dotted name ("www.iana.org") or an explicit port
    // ("localhost:8000"). Otherwise "domains/reserved" would read as a host.
    if (!hadAuthority && !/\./.test(authority) && !/:\d+$/.test(authority)) {
        return null;
    }

    return { authority, path, hadAuthority };
}

/**
 * Reduce an authority to the host and port a browser would actually use, so
 * two spellings of the same site compare equal. Returns null when the string
 * is not one this helper is willing to read as a host.
 *
 * Userinfo is dropped only when a scheme (or "//") proved this really is a
 * URL: the real host of "https://www.site.test@example.com/x" is example.com,
 * and reading it that way is what makes that URL fail the host check. Without
 * a scheme the same rule would swallow an email address — "support@site.test"
 * would read as the site's own root and wipe the field — so an "@" in a
 * schemeless string means "not a URL", and the paste is left alone.
 *
 * The host goes through the URL parser, which lowercases it and converts a
 * Unicode host to punycode, so a domain copied from the address bar matches a
 * punycode base_url. A leading "www." is dropped on both sides afterwards.
 */
function normalizeAuthority(authority, hadAuthority) {
    const at = authority.lastIndexOf('@');

    if (at !== -1 && !hadAuthority) {
        return null;
    }

    const hostAndPort = at === -1 ? authority : authority.slice(at + 1);

    let normalized;

    try {
        const parsed = new URL(`https://${hostAndPort}`);
        normalized = parsed.host;
    } catch {
        normalized = hostAndPort.toLowerCase();
    }

    return normalized.replace(/^www\./, '');
}

/**
 * Strip this site's base_url subdirectory off an already-parsed path.
 *
 * Mirrors Site::stripBaseSubdirectory(): the base segment is compared
 * case-insensitively (an origin may serve "/wida" for a base_url of "/WIDA")
 * while the remainder keeps its original case. Returns null when the path does
 * not sit under the base, which makes it a URL for some other part of the
 * domain rather than a page of this site.
 */
function stripBaseSubdirectory(path, base) {
    const withoutTrailingSlash = path.length > 1 ? path.replace(/\/$/, '') : path;

    if (withoutTrailingSlash.toLowerCase() === base.toLowerCase()) {
        return '';
    }

    if (path.slice(0, base.length + 1).toLowerCase() === `${base.toLowerCase()}/`) {
        return path.slice(base.length);
    }

    return null;
}

/**
 * Strip the site's domain off a pasted URL, or return null to leave it alone.
 *
 * `domain` is the prefix the field renders beside the input. Its trailing slash
 * decides the shape of the result, because that is the shape the field's value
 * is concatenated back into: "https://site.test/" yields "contact" (empty for
 * the site root), "https://site.test" yields "/contact" (and "/" for the root).
 */
export function stripDomainPrefix(pastedText, domain) {
    // Line breaks are dropped, not kept, because an <input> drops them too when
    // a value is assigned to it. Keeping them would leave the field showing one
    // string while the property the form submits held another.
    const pasted = (pastedText ?? '').replace(/[\r\n]/g, '').trim();
    const prefix = (domain ?? '').trim();

    if (pasted === '' || prefix === '') {
        return null;
    }

    const site = splitUrlish(prefix);
    const paste = splitUrlish(pasted);

    if (site === null || paste === null) {
        return null;
    }

    const siteHost = normalizeAuthority(site.authority, site.hadAuthority);
    const pasteHost = normalizeAuthority(paste.authority, paste.hadAuthority);

    if (siteHost === null || pasteHost === null || siteHost !== pasteHost) {
        return null;
    }

    const wantsLeadingSlash = !prefix.endsWith('/');
    const base = site.path.replace(/\/+$/, '');

    const queryOrFragment = paste.path.search(/[?#]/);
    // A browser reads a backslash in an http(s) path as a slash, so the page
    // "site.test\about" names is /about. Keeping the backslash would store a
    // path that resolves to //about instead.
    const pathOnly = (queryOrFragment === -1 ? paste.path : paste.path.slice(0, queryOrFragment))
        .replace(/\\/g, '/');
    const suffix = queryOrFragment === -1 ? '' : paste.path.slice(queryOrFragment);

    let remainder = pathOnly;

    if (base !== '') {
        remainder = stripBaseSubdirectory(pathOnly, base);

        if (remainder === null) {
            return null;
        }
    }

    if (wantsLeadingSlash) {
        remainder = remainder.startsWith('/') ? remainder : `/${remainder}`;
    } else {
        remainder = remainder.replace(/^\//, '');
    }

    return remainder + suffix;
}

/**
 * Handle a paste on a field that renders the site's domain as a prefix.
 *
 * Reads the pasted text from the clipboard rather than the field's value after
 * the fact, so the strip works wherever the caret is and whatever the field
 * already holds. When the paste is a URL on this site, the default paste is
 * cancelled and the field is replaced outright with the path; `apply` is then
 * called with the same value so the caller can push it to the server.
 *
 * Returns false, having done nothing, when the paste is anything else — the
 * browser then pastes it normally.
 */
export function handleDomainPaste(event, domain, apply) {
    let pastedText;

    try {
        pastedText = event?.clipboardData?.getData('text/plain') || event?.clipboardData?.getData('text');
    } catch {
        return false;
    }

    const stripped = stripDomainPrefix(pastedText, domain);

    if (stripped === null) {
        return false;
    }

    event.preventDefault();

    writeStrippedValue(event.target, stripped);

    if (typeof apply === 'function') {
        apply(stripped);
    }

    return true;
}

/**
 * Replace a field's whole value with the stripped path.
 *
 * Cancelling the paste means the browser no longer writes the value for us,
 * and assigning to `field.value` is not the same thing: it fires no `input`
 * event, so a screen reader announces nothing where the native paste used to
 * be announced, and it wipes the field's undo stack, so Ctrl+Z cannot recover
 * what the user had typed before pasting.
 *
 * `execCommand('insertText')` over a full selection is the standard way to
 * keep both. It is deprecated and unavailable outside a document, so the
 * direct assignment stays as the fallback, and the `input` event is dispatched
 * by hand there so the change reaches the event path either way.
 */
function writeStrippedValue(field, stripped) {
    let inserted = false;

    try {
        if (typeof field.select === 'function' && typeof document !== 'undefined' && document.execCommand) {
            field.select();
            inserted = document.execCommand('insertText', false, stripped) && field.value === stripped;
        }
    } catch {
        inserted = false;
    }

    if (!inserted) {
        field.value = stripped;

        if (typeof field.setSelectionRange === 'function') {
            try {
                field.setSelectionRange(stripped.length, stripped.length);
            } catch {
                // Some input types refuse selection ranges; the value is already set.
            }
        }

        try {
            field.dispatchEvent(new InputEvent('input', { bubbles: true, data: stripped, inputType: 'insertFromPaste' }));
        } catch {
            // No InputEvent constructor (or no DOM at all); the value still landed.
        }
    }
}
